import Konva from 'konva';
import { Rect, RectProps } from 'components/Canvas/Rect';
import { Group } from 'react-konva';
import { useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { noop } from 'utils/fp';
import debounce from 'lodash/debounce';
import { useCanvasStore } from 'modules/Canvas/contexts/CanvasStoreContext';
import { TransformLayer } from 'modules/Canvas/components/CanvasRegionList/TransformLayer';
import { CanvasRegionAnnotation, RectConfig } from 'modules/Canvas/helpers/types';
import { baseTheme } from 'styles/utils';

type EditableBboxProps = RectProps & {
  item: CanvasRegionAnnotation;
  shapeConfig: RectConfig;
  scale: number;
  shouldOverdrawWholeArea?: boolean;
  onChange?: (item: CanvasRegionAnnotation, boundingBox: CF.API.BoundingBox) => Promise<void | string> | void | null;
  isSelectedShape?: boolean;
};

export const EditableBbox = observer(
  ({ item, shapeConfig, scale, opacity, shouldOverdrawWholeArea, onChange = noop, ...restOfProps }: EditableBboxProps) => {
    const ref = useRef<Konva.Rect>(null);
    const canvasStore = useCanvasStore();

    const handleChange = useCallback(
      debounce(async (boundingBox: CF.API.BoundingBox) => {
        const response = await onChange?.(item, boundingBox);
        canvasStore.setPreventDrawingStatus(false);
        if (response === 'resetDrawingState') {
          canvasStore.setCreationType(undefined);
          canvasStore.drawing_initiateSelection(undefined);
          canvasStore.setCurrentRegionPane(undefined);
          canvasStore.setSelectedShape(null);
        }
      }, 200),
      [item, onChange],
    );

    const handleMouseDown = useCallback(() => {
      canvasStore.setPreventDrawingStatus(true);
    }, []);

    const handleMouseUp = useCallback(() => {
      canvasStore.setPreventDrawingStatus(false);
    }, []);

    return (
      <Group id={shapeConfig.key}>
        <Rect
          draggable
          ref={ref}
          labelProps={{
            scale: 0,
            opacity,
          }}
          scale={scale}
          {...shapeConfig}
          {...restOfProps}
          x={shapeConfig.x}
          y={shapeConfig.y}
          color={baseTheme.light.colors.primary600}
          opacity={opacity}
          strokeScaleEnabled={false}
          strokeWidth={2}
        >
          <TransformLayer
            update={handleChange}
            elementRef={ref}
            shouldOverdrawWholeArea={shouldOverdrawWholeArea}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          />
        </Rect>
      </Group>
    );
  },
);
